import { Coordinate } from "client/Misc/commonTypes";
import { clamp } from "client/Misc/utils";
import {
	CSSProperties,
	Dispatch,
	ReadonlyRefObject,
	SetStateAction,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from "react";
import { useSynchronizedRef } from "./useSynchronizedRef";

export type UsePanZoomResult = {
	zoom: (action: "in" | "out") => void;
	hasPointersDown: boolean;
};

export type Limits = {
	minScale?: number;
	maxScale?: number;
};

export function usePanZoom(
	setState: Dispatch<SetStateAction<PanZoomState>>,
	hostRef: ReadonlyRefObject<HTMLElement | null>,
	limits: Limits = {}
): UsePanZoomResult {
	const [hasPointersDown, setHasPointersDown] = useState<boolean>(false);
	const pointerTrackingMap: ReadonlyRefObject<Map<number, Coordinate>> = useRef(
		new Map<number, Coordinate>()
	);

	const limitsRef: ReadonlyRefObject<Limits> = useSynchronizedRef(limits);

	const executeZoom = useCallback(
		(position: Coordinate, percentage: number): void => {
			setState(
				(s): PanZoomState => {
					let newScale: number = s.scale + (s.scale * percentage);

					const limits: Limits = limitsRef.current;

					newScale = clamp(
						newScale,
						limits.minScale,
						limits.maxScale
					);

					const percentileScaleDifference = newScale / s.scale;
					const newTranslateX = (percentileScaleDifference * (s.translateX - position.x)) + position.x;
					const newTranslateY = (percentileScaleDifference * (s.translateY - position.y)) + position.y;

					return {
						scale: newScale,
						translateX: newTranslateX,
						translateY: newTranslateY
					};
				}
			);
		},
		[limitsRef, setState]
	);

	const wheelHandler = useCallback(
		(args: WheelEvent) => {
			const host: Element | null = args.currentTarget instanceof Element
				? args.currentTarget
				: null;

			if (!host) {
				return;
			}

			const { left, top } = host.getBoundingClientRect();

			const position: Coordinate = {
				x: args.clientX - left,
				y: args.clientY - top
			};

			const percentage: number = args.deltaY * -0.001;

			executeZoom(position, percentage);
		},
		[executeZoom]
	);

	const pointerDownHandler = useCallback(
		(args: PointerEvent): void => {
			setHasPointersDown(true);

			pointerTrackingMap.current
				.set(
					args.pointerId,
					{
						x: args.clientX,
						y: args.clientY
					}
				);
		},
		[]
	);

	const pointerUpHandler = useCallback(
		(args: PointerEvent): void => {
			pointerTrackingMap.current
				.delete(args.pointerId);

			setHasPointersDown(pointerTrackingMap.current.size > 0);
		},
		[]
	);

	const pointerMoveHandler = useCallback(
		(args: PointerEvent): void => {
			const { current: host } = hostRef;
			if (!host) {
				return;
			}

			const pointerPositions: Map<number, Coordinate> = pointerTrackingMap.current;
			const oldPosition = pointerPositions.get(args.pointerId);

			if (!oldPosition) {
				return;
			}

			const newPosition: Coordinate = {
				x: args.clientX,
				y: args.clientY
			};

			pointerPositions.set(args.pointerId, newPosition);

			if (pointerPositions.size === 2) {
				const otherPosition = [...pointerPositions.entries()]
					.find(([id, _]) => id !== args.pointerId)
					?.[1];

				if (!otherPosition) {
					return;
				}

				const pointBetweenTwoTouches: Coordinate = {
					x: (oldPosition.x + otherPosition.x) / 2,
					y: (oldPosition.y + otherPosition.y) / 2
				};

				const { left, top } = host.getBoundingClientRect();

				const position: Coordinate = {
					x: pointBetweenTwoTouches.x - left,
					y: pointBetweenTwoTouches.y - top
				};

				const oldDiff: number = Math.sqrt(
					Math.pow(oldPosition.x - otherPosition.x, 2) +
					Math.pow(oldPosition.y - otherPosition.y, 2)
				);

				const newDiff: number = Math.sqrt(
					Math.pow(newPosition.x - otherPosition.x, 2) +
					Math.pow(newPosition.y - otherPosition.y, 2)
				);

				const percentage: number = (newDiff - oldDiff) * 0.003;

				executeZoom(position, percentage);

			} else if (pointerPositions.size === 1) {
				const diffX = newPosition.x - oldPosition.x;
				const diffY = newPosition.y - oldPosition.y;

				setState(
					(m): PanZoomState => ({
						...m,
						translateX: m.translateX + diffX,
						translateY: m.translateY + diffY
					})
				);
			}
		},
		[
			executeZoom,
			hostRef,
			setState
		]
	);

	const zoomHandler = useCallback(
		(action: "in" | "out"): void => {
			const { current: host } = hostRef;
			if (!host) {
				return;
			}

			const { width, height } = host.getBoundingClientRect();

			const position: Coordinate = {
				x: width / 2,
				y: height / 2
			};

			const percentage: number = action === "in"
				? 0.1
				: -0.1;

			executeZoom(position, percentage);
		},
		[executeZoom, hostRef]
	);

	useLayoutEffect(
		(): (() => void) | void => {
			const { current: host } = hostRef;
			if (!host) {
				return;
			}

			const originalTouchAction: string = host.style
				.touchAction;

			host.style
				.touchAction = "none";

			host.addEventListener("wheel", wheelHandler);
			host.addEventListener("pointerdown", pointerDownHandler);

			return (): void => {
				host.removeEventListener("wheel", wheelHandler);
				host.removeEventListener("pointerdown", pointerDownHandler);

				host.style
					.touchAction = originalTouchAction;
			};
		},
		[
			hostRef,
			pointerDownHandler,
			wheelHandler
		]
	);

	useEffect(
		(): (() => void) => {
			document.addEventListener("pointermove", pointerMoveHandler);

			const pointerUpEvents = [
				"pointerup",
				"pointercancel",
				"pointerleave"
			] as const;

			for (const event of pointerUpEvents) {
				document.addEventListener(event, pointerUpHandler);
			}

			return (): void => {
				document.removeEventListener("pointermove", pointerMoveHandler);

				for (const event of pointerUpEvents) {
					document.removeEventListener(event, pointerUpHandler);
				}
			};
		},
		[pointerMoveHandler, pointerUpHandler]
	);

	return {
		zoom: zoomHandler,
		hasPointersDown
	};
}

export type PanZoomState = {
	scale: number;
	translateX: number;
	translateY: number;
};

export type Matrix = {
	scaleX: number;
	scaleY: number;
	skewX: number;
	skewY: number;
	translateX: number;
	translateY: number;
};

function panZoomStateToMatrix(state: PanZoomState): Matrix {
	const {
		scale,
		translateX,
		translateY
	} = state;

	return {
		scaleX: scale,
		scaleY: scale,
		skewX: 0,
		skewY: 0,
		translateX,
		translateY
	};
}

export function panZoomToTransformValue(state: PanZoomState): string {
	const matrix: Matrix = panZoomStateToMatrix(state);

	const {
		scaleX,
		scaleY,
		skewX,
		skewY,
		translateX,
		translateY
	} = matrix;

	return `matrix(${scaleX}, ${skewY}, ${skewX}, ${scaleY}, ${translateX}, ${translateY})`;
}

export function panZoomToStyle(state: PanZoomState): CSSProperties {
	return {
		transform: panZoomToTransformValue(state),
		transformOrigin: "top left"
	};
}
